import { events } from "@constants/tracking";
import { useThemeContext } from "@contexts/ThemeContext";
import { type ExamDetails, useExamContext, useExams } from "@features/exam";
import { formatDateToLocalizedString } from "@helpers/date";
import {
	buildType,
	currentAppVersion,
	versionCode,
} from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import useUserInfo from "@hooks/useUserInfo";
import {
	composeMessage,
	getPlatform,
	getPremiumContext,
	isSafeUrl,
} from "@memorang/helpers";
import { useCopyToClipboard } from "@memorang/ui";
import { useRouter } from "expo-router";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
/* -----------------Third parties--------------- */
import { Linking } from "react-native";
/* -----------------Helpers && Hooks--------------- */
import type { Section } from "../types";

const isDevelopment = process.env.NODE_ENV === "development";

type Callbacks = {
	toggleShowDatePicker: () => void;
	toggleShowSignOutDialog: () => void;
	toggleDarkMode: () => void;
	toggleShowDeleteFeedbackDialog: () => void;
	toggleShowPurchaseDialog: () => void;
	toggleShowFaqsDialog: () => void;
};

export const getPurchasedExams = (examsList: ExamDetails[]) => {
	const purchasedExams = examsList?.filter(({ exam: { bundles } }) => {
		return bundles.find((item) => {
			const { hasAccess } = getPremiumContext(item.metadata);
			return hasAccess;
		});
	});
	return purchasedExams;
};

export const getPurchasedProducts = (examsList: ExamDetails[]) => {
	const purchasedExams = getPurchasedExams(examsList);

	const purchasedProducts = purchasedExams?.flatMap(({ exam }) =>
		exam.bundles.filter((item) => {
			return getPremiumContext(item.metadata).hasAccess;
		}),
	);
	return purchasedProducts;
};
const useAccountSettings = ({
	toggleShowDatePicker,
	toggleShowSignOutDialog,
	toggleDarkMode,
	toggleShowDeleteFeedbackDialog,
	toggleShowPurchaseDialog,
	toggleShowFaqsDialog,
}: Callbacks) => {
	const router = useRouter();
	const { t } = useTranslation();

	const {
		currentExamContext: { examDate },
	} = useExamContext();

	const { email } = useClerkUser();

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const { copyToClipboard } = useCopyToClipboard();

	const userInfo = useUserInfo();
	const username = userInfo?.name;

	const { exams } = useExams();
	const { isDarkMode } = useThemeContext();
	const purchasedProducts = getPurchasedProducts(exams);

	const exam = useAppStore((store) => store.exam);
	const supportEmail = useAppStore((store) => store.supportEmail);
	const contactSupportUrl = useAppStore(
		(store) => store.app.links.contactSupportUrl,
	);

	const privacyLink = useAppStore((store) => store.app.links.privacy);
	const tosLink = useAppStore((store) => store.app.links.tos);
	const getAccountSections = (): Section[] => {
		return [
			...(exam?.enabled
				? [
						// {
						// 	sectionTitle: "Current Exam",
						// 	items: [
						// 		{
						// 			icon: currentExam.icon || "file-document-outline",
						// 			type: "exam-filter",
						// 			title: currentExam.name,
						// 			iconRight: "swap-horizontal",
						// 		},
						// 	],
						// },
						// ...(productMetadata?.subscription?.endDate
						// 	? [
						// 			{
						// 				sectionTitle: t("account.currentExam"),
						// 				items: [
						// 					{
						// 						icon: "https://icon.memorang.com/icon/toefl_ibt_prep.png",
						// 						type: "extend-subscription",
						// 						title: productDetails?.name || "",
						// 						description: `${t("account.validUntil", {
						// 							date: DateTime.fromISO(
						// 								productMetadata.subscription.endDate,
						// 							).toLocaleString(DateTime.DATE_FULL),
						// 						})}`,
						// 						onPress: isWeb
						// 							? toggleShowPurchaseDialog
						// 							: () => {
						// 									router.push({
						// 										pathname: "/purchase",
						// 										params: {
						// 											productType: "STUDY_PACK",
						// 										},
						// 									});
						// 								},
						// 					},
						// 				],
						// 			},
						// 		]
						// 	: []),
						{
							sectionTitle: t("account.examDate"),
							items: [
								{
									icon: "calendar-outline",
									type: "datepicker",
									title: examDate
										? formatDateToLocalizedString(examDate)
										: t("account.selectDate"),
									onPress: toggleShowDatePicker,
									iconRight: "pencil-outline",
								},
							],
						},
						...(purchasedProducts.length > 0
							? [
									{
										sectionTitle: "Entitlements",
										items: purchasedProducts.map(
											({
												bundle: {
													name,
													icon: { url },
												},
												metadata,
											}) => {
												return {
													icon: url,
													title: name,
													description: `${t("account.validUntil", {
														date: DateTime.fromISO(
															metadata.subscription.endDate,
														).toLocaleString(DateTime.DATE_FULL),
													})}`,
												};
											},
										),
									},
								]
							: []),
						{
							sectionTitle: t("account.appearance"),
							items: [
								{
									icon: isDarkMode ? "weather-sunny" : "weather-night",
									title: t("account.darkMode"),
									type: "toggle",
									onPress: toggleDarkMode,
								},
							],
						},
						{
							sectionTitle: t("account.accountInfo"),
							items: [
								// {
								// 	icon: "account-outline",
								// 	title: t("account.name"),
								// 	description: username || "",
								// },
								{
									icon: "email-outline",
									title: t("account.email"),
									description: email,
								},
								{
									onPress: () => {
										router.push("/onboarding");
									},
									icon: "map-marker-path",
									title: t("account.restartOnboardingFlow"),
									iconRight: "chevron-right",
								},
							],
						},
					]
				: []),
			...(isDevelopment
				? [
						{
							sectionTitle: t("account.dev"),
							items: [
								{
									onPress: () => {
										router.push("/sandbox");
									},
									icon: "code-json",
									title: t("account.sandbox"),
								},
							],
						},
					]
				: []),
			{
				sectionTitle: t("account.about"),
				items: [
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.termsOfServiceClicked,
							});
							Linking.openURL(tosLink);
						},
						icon: "gavel",
						title: t("account.termsOfService"),
						iconRight: "open-in-new",
					},
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.privacyPolicyClicked,
							});
							Linking.openURL(privacyLink);
						},
						icon: "eye-outline",
						title: t("account.privacyPolicy"),
						iconRight: "open-in-new",
					},
					{
						onPress: async () => {
							explicitCallTrackCustomEvent({
								eventName: events.contactSupportClicked,
								username,
								email,
							});

							if (contactSupportUrl && isSafeUrl(contactSupportUrl)) {
								Linking.openURL(contactSupportUrl);
								return;
							}

							const platform = getPlatform();
							const subject = `${buildType} ${t("account.support")} | ${platform} | ${username}`;
							const body = t("account.supportEmailBody", { username, email });

							if (isWeb) {
								await copyToClipboard(
									supportEmail,
									t("common.copiedToClipboard"),
								);
							}

							composeMessage(subject, body, supportEmail);
						},
						icon: "help-circle-outline",
						title: t("account.contactSupport"),
						rightText:
							isWeb && !isSafeUrl(contactSupportUrl) ? supportEmail : undefined,
						iconRight: "open-in-new",
					},
					// {
					// 	onPress: isWeb
					// 		? toggleShowFaqsDialog
					// 		: () => {
					// 				router.push("/faqs");
					// 			},
					// 	icon: "message-question-outline",
					// 	title: t("account.frequentlyAskedQuestions"),
					// },
					{
						onPress: toggleShowSignOutDialog,
						icon: "logout",
						title: t("account.signOut"),
					},
					{
						onPress: toggleShowDeleteFeedbackDialog,
						icon: "account-remove-outline",
						title: t("account.deleteAccount"),
					},
				],
			},
			{
				sectionTitle: t("account.platform"),
				items: [
					// TODO: We are not showing partnerships for TOEFL
					// {
					// 	onPress: () => {
					// 		Linking.openURL(urls.core);
					// 	},
					// 	icon: "web",
					// 	title: t("account.partnerships"),
					// 	iconRight: "open-in-new",
					// },
					{
						icon: "unfold-more-vertical",
						title: `${currentAppVersion}${versionCode ? ` (${versionCode})` : ""}`,
						// onPress: () => {
						// 	// TODO: Add functionality for version information
						// },
					},
				],
			},
		];
	};
	return {
		isLoading: false,
		getAccountSections,
		usernameValue: username,
		actualUsernameValue: username,
	};
};

export default useAccountSettings;
